import { useState } from 'react';
import { IconAnchor, IconBox, IconChevronDown, IconChevronLeft, IconClock, IconDots, IconDownload, IconLock, IconMail, IconMinus, IconPlus, IconRefresh, IconRoute, IconShip, IconUpload, IconUser, IconArrowsMaximize, IconChevronRight, IconCheck, IconClipboardList, IconFileText, IconDeviceFloppy } from '@tabler/icons-react';
import { Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Chip, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { DOC, FILES, IMP, MAIL, RQF, SECS, VSF, buildRows, needLook, type Edits, type RowView, type Seg, type Target } from './aireadData';
import { useT } from '@hana/i18n';
import './i18n';
import s from './airead.module.css';

// Mint-AIRead (1536 · -1280 · -1920 · -iPad): AI đọc chứng từ → 2 form song song, duyệt từng ô. Kết quả đọc giả lập; nút ghi báo toast.

type Flt = 'all' | 'look' | 'lech' | 'trong';

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}

const GLYPH: Record<string, string> = { khop: '=', lech: '→', trong: '→', tay: '—', lock: '' };
const ARIA: Record<string, string> = { khop: 'ar.aMatch', lech: 'ar.aDiff', trong: 'ar.aEmpty', lock: 'ar.aLock', tay: 'ar.aHand' };

export function AiRead() {
  const t = useT();
  const NO_WRITE = t('airead.noWrite');
  const pad = useLayout() === 'pad';
  const toast = useToast();
  const state = readState();
  const noW = state === 'noperm';
  const [target, setTarget] = useState<Target>('rq');
  const [dense, setDense] = useState<'std' | 'dense'>('std');
  const [e, setE] = useState<Edits>({ fv: {}, cp: {}, okm: {}, ed: {} });
  const [flt, setFlt] = useState<Flt>('all');
  const [hov, setHov] = useState('');
  const [acc, setAcc] = useState<Record<string, boolean>>({});
  const [fi, setFi] = useState(0);
  const [fmOpen, setFmOpen] = useState(false);
  const [mnOpen, setMnOpen] = useState(false);
  const [lm, setLm] = useState<'file' | 'text'>('file');
  const [zoom, setZoom] = useState(100);
  const [pg, setPg] = useState(1);
  const say = (msg: string) => toast({ msg });

  const isRq = target === 'rq';
  const defs = isRq ? RQF : VSF;
  const ns = (k: string) => `${target}:${k}`;
  const rows = buildRows(defs, e, ns);
  const cnt = { all: rows.length, look: rows.filter(needLook).length, lech: rows.filter((r) => r.st === 'lech').length, trong: rows.filter((r) => r.st === 'trong').length };
  const pass = (r: RowView) => flt === 'all' || (flt === 'look' ? needLook(r) : r.st === flt);
  const fillable = rows.filter((r) => r.st === 'trong' && r.def[7] === 'cp');
  const file = FILES[target][Math.min(fi, FILES[target].length - 1)]!;
  const pages = file[3];
  const page = Math.min(pg, pages);
  const mode = lm === 'text' || file[0] === 'MAIL' ? 'text' : 'file';
  const lookLeft = rows.filter((r) => r.look).length;
  const changed = rows.filter((r) => r.cur.trim().toUpperCase() !== r.def[4].trim().toUpperCase()).length;

  const guard = (fn: () => void) => () => (noW ? say(t('ar.viewOnly')) : fn());
  const copy = (k: string) => {
    const r = rows.find((x) => x.def[0] === k);
    if (!r || !r.def[3]) return;
    setE((p) => ({ ...p, fv: { ...p.fv, [ns(k)]: r.def[3] }, cp: { ...p.cp, [ns(k)]: r.cur } }));
    setHov(k);
  };
  const undo = (r: RowView) => {
    const k = ns(r.def[0]);
    setE((p) => {
      const fv = { ...p.fv }, cp = { ...p.cp };
      if (r.ai) { fv[k] = ''; cp[k] = r.def[3]; } else { if (cp[k] === r.def[4]) delete fv[k]; else fv[k] = cp[k] ?? ''; delete cp[k]; }
      return { ...p, fv, cp };
    });
  };
  const fillAll = guard(() => {
    if (!fillable.length) return say(t('ar.noEmptyFill'));
    setE((p) => {
      const fv = { ...p.fv }, cp = { ...p.cp };
      fillable.forEach((r) => { cp[ns(r.def[0])] = r.cur; fv[ns(r.def[0])] = r.def[3]; });
      return { ...p, fv, cp };
    });
    say(t('ar.copied', { n: fillable.length }));
  });
  const confirmHigh = guard(() => {
    setE((p) => {
      const okm = { ...p.okm };
      rows.forEach((r) => { if (r.def[7] !== 'tay' && r.def[3] && r.def[5] >= 80) okm[ns(r.def[0])] = true; });
      return { ...p, okm };
    });
    say(t('ar.confirmedHigh'));
  });
  const save = guard(() => {
    if (lookLeft) { setFlt('look'); return say(t('ar.lookLeft', { n: lookLeft })); }
    say(NO_WRITE);
  });
  const pickTarget = (k: Target) => { setTarget(k); setFlt('all'); setHov(''); setFi(0); setPg(1); setMnOpen(false); setFmOpen(false); };

  const segs = (list: Seg[][]) => list.map((ln, i) => (
    <div key={i} className={`${s.ln} ${ln.some((g) => g.big) ? s.big : ''}`}>
      {ln.map((g, j) => {
        if (!g.k) return <span key={j} style={g.b ? { fontWeight: 600 } : undefined}>{g.x || ' '}</span>;
        const r = rows.find((x) => x.def[0] === g.k);
        const cls = hov === g.k ? s.hit : r?.look ? s.hitLook : s.hitOn;
        return <span key={j} className={cls} title={r?.def[2]} onMouseEnter={() => setHov(g.k!)} onClick={() => { setHov(g.k!); setFlt('all'); }}>{g.x}</span>;
      })}
    </div>
  ));

  const sub: [React.ReactNode, string, string][] = isRq
    ? [[<IconUser size={13} aria-hidden="true" />, 'ARRIVE', t('ar.customer')], [<IconRoute size={13} aria-hidden="true" />, 'VNSGN → KRKAN', t('ar.route')], [<IconShip size={13} aria-hidden="true" />, 'YML', t('ar.carrier')], [<IconBox size={13} aria-hidden="true" />, '15x40HC', t('ar.container')], [<IconMail size={13} aria-hidden="true" />, 'info@ 21/Oct 10:42', t('ar.srcMail')]]
    : [[<IconAnchor size={13} aria-hidden="true" />, 'VNSGN · CAT LAI', t('ar.port')], [<IconClock size={13} aria-hidden="true" />, rows.find((r) => r.def[0] === 'etd')!.cur, 'ETD'], [<IconClipboardList size={13} aria-hidden="true" />, t('ar.jobN', { n: IMP.length }), t('ar.jobCount')]];

  const menu: [string, () => void][] = [
    [isRq ? t('ar.mSave') : t('ar.mSaveVs'), save],
    ...(fillable.length ? [[t('ar.mFill', { n: fillable.length }), fillAll] as [string, () => void]] : []),
    [t('ar.mConfHigh'), confirmHigh],
    [t('ar.mReread'), () => say(t('ar.reread', { f: file[1] }))],
    [t('ar.mDiscard'), () => { setE((p) => ({ ...p, fv: {}, cp: {}, ed: {}, okm: {} })); say(t('ar.discarded', { n: changed })); }],
    [t('ar.mOne'), () => say(isRq ? t('ar.open1rq') : t('ar.open1vs'))],
  ];

  const header = (
    <PageHeader
      lead={<a href="/ezy/booking" className={s.back} aria-label={t('ar.backBooking')} title={t('ar.backBooking')}><IconChevronLeft size={18} aria-hidden="true" /></a>}
      title={isRq ? t('ar.titleRq') : t('ar.titleVs')}
      meta={isRq ? <><Chip tone="warn" dot="var(--warn)">RQ Standby</Chip><Chip dot="var(--d-blue)">OB DC</Chip></> : <><Chip tone="info" dot="var(--d-blue)">YML</Chip><Chip dot="var(--lb)">PS3</Chip></>}
      sub={<span className={s.sub}>{sub.map(([ic, k, tip]) => <span key={tip} title={tip} className={s.subI}>{ic}{k}</span>)}</span>}
      findHint={false}
      noFind={pad}
      tabs={
        <div className={s.hTools}>
          <Segmented label={t('ar.formTarget')} value={target} onChange={pickTarget} items={[{ key: 'rq', label: t('ar.tRq') }, { key: 'vessel', label: t('ar.tVs') }]} />
          {!pad && <Segmented label={t('ar.density')} kind="toggle" value={dense} onChange={setDense} items={[{ key: 'std', label: t('ar.dStd') }, { key: 'dense', label: t('ar.dDense') }]} />}
        </div>
      }
      actions={
        <>
          <Button variant="primary" icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={save}>{t('ar.save')}</Button>
          <div className={s.pop}>
            <Button aria-label={pad ? t('ar.moreActionsPad') : t('ar.moreActions')} aria-haspopup="menu" aria-expanded={mnOpen} icon={<IconDots size={18} aria-hidden="true" />} onClick={() => { setMnOpen((o) => !o); setFmOpen(false); }} />
            {mnOpen && (
              <div role="menu" aria-label={t('ar.actions')} className={s.menu}>
                {menu.map(([k, go]) => <button key={k} type="button" role="menuitem" className={s.mi} onClick={() => { setMnOpen(false); go(); }}>{k}</button>)}
              </div>
            )}
          </div>
        </>
      }
    />
  );

  if (state !== 'ready' && state !== 'noperm') {
    return (
      <Page kind="table">
        {header}
        {state === 'loading' && <div className={s.grid}><div className={s.card}><Skeleton /></div><div className={s.card}><Skeleton /></div></div>}
        {state === 'empty' && <div className={s.stCard}><StateView state="empty" title={t('ar.emptyT')} action={<Button icon={<IconUpload size={16} aria-hidden="true" />} onClick={() => say(NO_WRITE)}>{t('ar.upload')}</Button>} /></div>}
        {state === 'error' && <div className={s.stCard}><StateView state="error" title={t('ar.errT')} action={<Button icon={<IconFileText size={16} aria-hidden="true" />} onClick={() => location.reload()}>{t('ar.reread2')}</Button>} /></div>}
      </Page>
    );
  }

  return (
    <Page kind="table">
      {header}
      {noW && <div role="note" className={s.note}><IconLock size={16} aria-hidden="true" />{t('ar.roNote')}</div>}
      <div className={`${s.grid} ${dense === 'dense' && !pad ? s.dense : ''} ${pad ? s.pad : ''}`}>
        <section aria-label={t('ar.srcFile')} className={s.card} onMouseLeave={() => setHov('')}>
          <div className={s.bar}>
            <div className={s.pop} style={{ flex: '1 1 auto', minWidth: 0 }}>
              <button type="button" className={s.fileBtn} aria-haspopup="menu" aria-expanded={fmOpen} onClick={() => { setFmOpen((o) => !o); setMnOpen(false); }}>
                <span className={s.ext}>{file[0]}</span>
                <span className={s.ell}>{file[1]}</span>
                <IconChevronDown size={18} aria-hidden="true" className={s.chev} />
              </button>
              {fmOpen && (
                <div role="menu" aria-label={t('ar.swapFile')} className={s.fmenu}>
                  {FILES[target].map((f, i) => (
                    <button key={f[1]} type="button" role="menuitemradio" aria-checked={i === fi} className={s.mi} onClick={() => { setFi(i); setPg(1); setFmOpen(false); say(t('ar.reread', { f: f[1] })); }}>
                      <span className={s.mark}>{i === fi ? '✓' : ''}</span><span className={s.ell}>{f[2]} · {f[1]}</span>
                    </button>
                  ))}
                  <div className={s.sep} />
                  <button type="button" role="menuitem" className={s.mi} onClick={guard(() => { setFmOpen(false); say(t('ar.uploaded')); })}><IconUpload size={16} aria-hidden="true" />{t('ar.upload')}</button>
                </div>
              )}
            </div>
            <Segmented label={t('ar.viewMode')} value={lm} onChange={setLm} items={[{ key: 'file', label: t('ar.srcFile') }, { key: 'text', label: t('ar.readable') }]} />
            <Button variant="ghost" aria-label={t('ar.reread2')} icon={<IconRefresh size={18} aria-hidden="true" />} onClick={() => say(t('ar.rereadN', { f: file[1], n: rows.filter((r) => r.def[3]).length }))} />
            <a href="/ezy/files/view" className={s.ib} aria-label={t('ar.expand')} title={t('ar.expand')}><IconArrowsMaximize size={18} aria-hidden="true" /></a>
            <Button variant="ghost" aria-label={t('ar.download')} icon={<IconDownload size={18} aria-hidden="true" />} onClick={() => say(t('ar.downloaded', { f: file[1] }))} />
          </div>
          <div className={s.zbar}>
            <Button variant="ghost" aria-label={t('ar.zoomOut')} icon={<IconMinus size={18} aria-hidden="true" />} onClick={() => setZoom((z) => Math.max(50, z - 25))} />
            <span className={s.zk}>{zoom}%</span>
            <Button variant="ghost" aria-label={t('ar.zoomIn')} icon={<IconPlus size={18} aria-hidden="true" />} onClick={() => setZoom((z) => Math.min(200, z + 25))} />
            <Button variant="ghost" size="sm" icon={<IconArrowsMaximize size={16} aria-hidden="true" />} onClick={() => setZoom(100)}>{t('ar.fit')}</Button>
            {pages > 1 && (
              <div className={s.pgs}>
                <Button variant="ghost" aria-label={t('ar.prevPage')} disabled={page <= 1} icon={<IconChevronLeft size={18} aria-hidden="true" />} onClick={() => setPg(page - 1)} />
                <span className={s.pk}>{page} / {pages}</span>
                <Button variant="ghost" aria-label={t('ar.nextPage')} disabled={page >= pages} icon={<IconChevronRight size={18} aria-hidden="true" />} onClick={() => setPg(page + 1)} />
              </div>
            )}
          </div>
          <div className={s.view}>
            {mode === 'file' ? (
              <div style={{ width: Math.round((520 * zoom) / 100), height: Math.round((700 * zoom) / 100), margin: '0 auto', flexShrink: 0 }}>
                <div role="img" aria-label={t('ar.pageAria', { f: file[1], p: page, n: pages })} className={s.paper} style={{ transform: `scale(${zoom / 100})` }}>
                  {segs(file[0] === 'PDF' ? DOC[target][page] ?? [] : [])}
                </div>
              </div>
            ) : (
              <div className={s.text}>{segs(file[0] === 'MAIL' ? MAIL[target] : DOC[target][page] ?? [])}</div>
            )}
          </div>
        </section>

        <section aria-label={isRq ? t('ar.secRq') : t('ar.secVs')} className={s.card}>
          <div className={s.bar} style={{ flexWrap: 'wrap' }}>
            <h2 className={s.h2}>{isRq ? t('ar.secRq') : t('ar.secVs')}</h2>
            <span style={{ flexGrow: 1 }} />
            <Segmented label={t('ar.fltCell')} value={flt} onChange={setFlt} items={[{ key: 'all', label: t('ar.fAll'), n: cnt.all }, { key: 'look', label: t('ar.fLook'), n: cnt.look }, { key: 'lech', label: t('ar.fDiff'), n: cnt.lech }, { key: 'trong', label: t('ar.fEmpty'), n: cnt.trong }]} />
            {fillable.length > 0 && <Button size="sm" onClick={fillAll}>{t('ar.mFill', { n: fillable.length })}</Button>}
          </div>
          <div className={s.form}>
            {SECS[target].map(([sk, sl]) => {
              const all = rows.filter((r) => r.def[1] === sk);
              const shown = all.filter(pass);
              if (!shown.length && flt !== 'all') return null;
              const open = acc[sk] !== false;
              const looks = all.filter(needLook).length;
              return (
                <section key={sk} aria-label={sl} className={s.sec}>
                  <button type="button" className={s.secH} aria-expanded={open} onClick={() => setAcc({ ...acc, [sk]: !open })}>
                    <b>{sl}</b><span className={s.lb12}>{all.length}</span>
                    {looks > 0 && <span title={t('ar.fLook')} className={s.look}><span className={s.lookDot} />{looks}</span>}
                    <IconChevronDown size={18} aria-hidden="true" className={`${s.chev} ${open ? s.up : ''}`} />
                  </button>
                  {open && shown.map((r) => <Row key={r.def[0]} t={t} r={r} on={hov === r.def[0]} noW={noW} onHov={() => { setHov(r.def[0]); if (r.def[6] && r.def[6] !== page) setPg(r.def[6]); }} onCopy={guard(() => copy(r.def[0]))} onUndo={guard(() => undo(r))} onOk={guard(() => setE((p) => ({ ...p, okm: { ...p.okm, [ns(r.def[0])]: !p.okm[ns(r.def[0])] } })))} onType={(v) => !noW && setE((p) => ({ ...p, fv: { ...p.fv, [ns(r.def[0])]: v }, ed: { ...p.ed, [ns(r.def[0])]: true } }))} />)}
                </section>
              );
            })}
            {!rows.some(pass) && <div className={s.none}>{t('ar.noneLeft')}</div>}
            {!isRq && (
              <section aria-label={t('ar.impact')} className={s.imp}>
                <div className={s.impH}><IconClipboardList size={16} aria-hidden="true" /><b>{t('ar.impact')}</b><span className={s.lb12}>{t('ar.jobN', { n: IMP.length })}</span></div>
                {IMP.map(([job, cus, ty, cn, st]) => (
                  <div key={job} className={s.impR}>
                    <a href="/ezy/jobs">{job}</a><span className={s.ell}>{cus}</span><span className={s.tyk}>{ty}</span><span className={s.lb12}>{cn}</span>
                    <span title={st} className={s.etd}><span className={s.cdot} style={{ background: st === 'BK Standby' ? 'var(--warn)' : 'var(--d-blue)' }} />ETD 18/Nov → 19/Nov</span>
                  </div>
                ))}
              </section>
            )}
          </div>
        </section>
      </div>
    </Page>
  );
}

function Row({ t, r, on, noW, onHov, onCopy, onUndo, onOk, onType }: { t: (k: string, v?: Record<string, string | number>) => string; r: RowView; on: boolean; noW: boolean; onHov: () => void; onCopy: () => void; onUndo: () => void; onOk: () => void; onType: (v: string) => void }) {
  const [k, , label, read, , conf, , kind] = r.def;
  const lo = !!conf && conf < 80;
  const aria = `${t(ARIA[r.st]!)} · ${label}`;
  const go = r.st === 'lech' || r.st === 'trong';
  const stCls = `${s.st} ${s[`st_${r.st}`]}`;
  const inCls = `${s.inp} ${r.look ? s.inLook : r.st === 'lech' ? s.inLech : r.copied ? s.inCp : ''}`;
  return (
    <div className={`${s.row} ${on ? s.rowOn : ''}`} onMouseEnter={onHov}>
      <label htmlFor={`ar-${k}`} className={s.lab}>{label}</label>
      {go ? <button type="button" className={stCls} aria-label={aria} title={aria} onClick={onCopy}>{GLYPH[r.st]}</button>
        : r.st === 'lock' ? <span role="img" className={stCls} aria-label={aria} title={aria}><IconLock size={14} aria-hidden="true" /></span>
        : <span role="img" className={stCls} aria-label={aria} title={aria}>{GLYPH[r.st]}</span>}
      <div className={s.cell}>
        <input id={`ar-${k}`} className={inCls} value={r.cur} placeholder={kind === 'tay' ? t('ar.hand') : ''} readOnly={noW} onFocus={onHov} onChange={(ev) => onType(ev.target.value)} />
        {(r.st === 'lech' || r.st === 'lock') && <span className={s.hint} style={{ color: r.st === 'lech' ? 'var(--warn)' : 'var(--lb)' }}>{t('ar.fileVal', { v: read })}</span>}
        {r.copied && <button type="button" className={s.undo} aria-label={t('ar.undoCopyAria')} onClick={onUndo}>{r.ai ? t('ar.aiFilled') : r.cur === '' ? t('ar.removed') : t('ar.copiedUndo')}</button>}
      </div>
      {!!read && kind !== 'tay' ? <span title={t('ar.confidence', { c: conf })} className={s.conf} style={{ color: lo ? 'var(--warn)' : conf >= 90 ? 'var(--ok)' : 'var(--tx2)' }}><span className={s.cdot} style={{ background: lo ? 'var(--warn)' : conf >= 90 ? 'var(--ok)' : 'var(--d-blue)' }} />{conf}%</span> : <span />}
      {!!read && kind !== 'tay' ? <button type="button" className={`${s.ok} ${r.ok ? s.okOn : ''}`} aria-pressed={r.ok} aria-label={r.ok ? t('ar.confirmedL', { l: label }) : t('ar.confirmL', { l: label })} title={r.ok ? t('ar.confirmedL', { l: label }) : t('ar.confirmL', { l: label })} onClick={onOk}><IconCheck size={16} aria-hidden="true" /></button> : <span />}
    </div>
  );
}
